/**
 * Recovery mode composable for handling socket disconnection during processing
 * This is a simplified version for v2 - provides state inspection for the dev panel
 */
import { ref, readonly, computed } from "vue";
import { shouldSimulatePollingTimeout } from "./useDevSettings";
import { logRecoveryEvent } from "./useEventLog";
import { isLocalEnv } from "@/utils/IdentifyGlobalEnv";

// Recovery mode state stored in memory (resets on page refresh - which is desired)
const RECOVERY_TIMEOUT_MS_NORMAL = 150000; // 2.5 minutes
const RECOVERY_TIMEOUT_MS_DEV = 15000; // 15 seconds for dev testing
const POLLING_INTERVAL_MS = 5000; // 5 seconds

// Dynamic timeout based on dev settings
function getRecoveryTimeoutMs(): number {
  return shouldSimulatePollingTimeout() ? RECOVERY_TIMEOUT_MS_DEV : RECOVERY_TIMEOUT_MS_NORMAL;
}

// Statuses that constitute "active processing flow" - when socket disconnect triggers recovery
// V2 uses different status names than V1 (e.g., "Recorded" vs "recorded")
const ACTIVE_PROCESSING_STATUSES = ["recorded", "transcribing", "transcribed", "analyzing"];

// Terminal statuses that indicate processing is complete (success or failure)
// V2 uses "Summarized" instead of "analyzed"
const TERMINAL_SUCCESS_STATUSES = ["summarized", "analyzed", "verified"];
const TERMINAL_FAILURE_STATUSES = ["error", "interrupted", "discarded"];

// Status-specific messages for the recovery modal
const STATUS_MESSAGES: Record<string, string> = {
  recorded: "Enviando áudio...",
  transcribing: "Transcrevendo áudio...",
  transcribed: "Gerando resumo...",
  analyzing: "Quase pronto...",
  summarized: "Resumo gerado!",
};

// Singleton state (shared across all component instances)
const isInRecoveryMode = ref(false);
const recoveryReason = ref<string | null>(null);
const lastKnownStatus = ref<string | null>(null);
const showRefreshButton = ref(false);
const pollingSessionId = ref<string | null>(null);
const pollingFailCount = ref(0);
const recoveryStartTime = ref<number | null>(null);

// Internal timers
let pollingIntervalId: ReturnType<typeof setInterval> | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Check if a status is in the active processing flow range
 * These are statuses where socket disconnect should trigger recovery mode
 */
export function isInActiveProcessingFlow(status: string | null): boolean {
  if (!status) return false;
  return ACTIVE_PROCESSING_STATUSES.includes(status.toLowerCase());
}

/**
 * Get the recovery message for a specific status
 */
export function getRecoveryMessage(status: string | null): string {
  if (!status) return "Processando sua gravação...";
  return STATUS_MESSAGES[status.toLowerCase()] || "Processando sua gravação...";
}

export function useRecoveryMode() {
  // Computed properties
  const recoveryMessage = computed(() => getRecoveryMessage(lastKnownStatus.value));

  const elapsedTime = computed(() => {
    if (!recoveryStartTime.value) return 0;
    return Date.now() - recoveryStartTime.value;
  });

  /**
   * Activate recovery mode
   * @param reason - Why recovery was triggered (e.g., "socket_disconnect")
   * @param status - Last known status when recovery was triggered
   * @param sessionId - Session ID for polling
   */
  const activateRecovery = (reason: string, status: string, sessionId: string) => {
    if (isInRecoveryMode.value) {
      return;
    }

    // Log activation event for dev panel
    if (isLocalEnv()) {
      logRecoveryEvent("activated", { reason, status, sessionId });
    }

    isInRecoveryMode.value = true;
    recoveryReason.value = reason;
    lastKnownStatus.value = status;
    pollingSessionId.value = sessionId;
    showRefreshButton.value = false;
    pollingFailCount.value = 0;
    recoveryStartTime.value = Date.now();

    // Start timeout for showing refresh button
    const timeoutMs = getRecoveryTimeoutMs();
    timeoutId = setTimeout(() => {
      showRefreshButton.value = true;

      // Log timeout event for dev panel
      if (isLocalEnv()) {
        logRecoveryEvent("timeout_elapsed", { timeoutMs });
      }
    }, timeoutMs);
  };

  /**
   * Deactivate recovery mode (success or manual exit)
   */
  const deactivateRecovery = (_reason = "manual") => {
    if (!isInRecoveryMode.value) return;

    // Log deactivation event for dev panel
    if (isLocalEnv()) {
      const elapsedMs = recoveryStartTime.value ? Date.now() - recoveryStartTime.value : 0;
      logRecoveryEvent("deactivated", { reason: _reason, elapsedMs });
    }

    // Clear timers
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    // Reset state
    isInRecoveryMode.value = false;
    recoveryReason.value = null;
    lastKnownStatus.value = null;
    pollingSessionId.value = null;
    showRefreshButton.value = false;
    pollingFailCount.value = 0;
    recoveryStartTime.value = null;
  };

  /**
   * Update the last known status (from polling)
   */
  const updateStatus = (status: string) => {
    lastKnownStatus.value = status;
  };

  /**
   * Start HTTP polling for session status
   * V2 API returns: { recorderStatus: "Summarized", summaryData: "...", ... }
   * 
   * @param fetchStatusFn - Async function that fetches current status from API
   * @param onStatusComplete - Callback when status reaches terminal success state (summarized/analyzed)
   */
  const startPolling = (
    fetchStatusFn: () => Promise<{ status: string; recorderStatus?: string; data?: unknown; summaryData?: string } | null>,
    onStatusComplete: (data: unknown) => void
  ) => {
    if (pollingIntervalId) {
      return;
    }

    const poll = async () => {
      try {
        // Dev mode: simulate polling timeout by never reaching terminal status
        if (shouldSimulatePollingTimeout()) {
          updateStatus("analyzing");
          return;
        }

        const result = await fetchStatusFn();

        if (!result) {
          pollingFailCount.value++;
          return;
        }

        pollingFailCount.value = 0; // Reset on success

        // V2 API uses recorderStatus, V1 uses status - support both
        const currentStatus = (result.recorderStatus || result.status || "").toLowerCase();
        updateStatus(currentStatus);

        // Check if we've reached a terminal success status (V2: "summarized", V1: "analyzed")
        if (TERMINAL_SUCCESS_STATUSES.includes(currentStatus)) {
          // Log success event for dev panel
          if (isLocalEnv()) {
            logRecoveryEvent("polling_success", { status: currentStatus });
          }

          stopPolling();
          onStatusComplete(result);
          deactivateRecovery("status_complete");
        }
        
        // Check for terminal failure statuses
        if (TERMINAL_FAILURE_STATUSES.includes(currentStatus)) {
          if (isLocalEnv()) {
            logRecoveryEvent("polling_failure", { status: currentStatus });
          }
          
          stopPolling();
          deactivateRecovery("status_failed");
        }
      } catch (error) {
        pollingFailCount.value++;
        console.error("[Recovery] Polling error:", error);
      }
    };

    // Immediate first poll
    poll();

    // Set up interval
    pollingIntervalId = setInterval(poll, POLLING_INTERVAL_MS);
  };

  /**
   * Stop HTTP polling
   */
  const stopPolling = () => {
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }
  };

  /**
   * Handle page refresh action
   */
  const handleRefresh = () => {
    deactivateRecovery("user_refresh");
    window.location.reload();
  };

  return {
    // State (readonly)
    isInRecoveryMode: readonly(isInRecoveryMode),
    recoveryReason: readonly(recoveryReason),
    lastKnownStatus: readonly(lastKnownStatus),
    showRefreshButton: readonly(showRefreshButton),
    pollingFailCount: readonly(pollingFailCount),

    // Computed
    recoveryMessage,
    elapsedTime,

    // Actions
    activateRecovery,
    deactivateRecovery,
    updateStatus,
    startPolling,
    stopPolling,
    handleRefresh,

    // Utilities
    isInActiveProcessingFlow,
    getRecoveryMessage,

    // Constants (for external use)
    ACTIVE_PROCESSING_STATUSES,
    TERMINAL_SUCCESS_STATUSES,
    TERMINAL_FAILURE_STATUSES,
  };
}
